import Box from "@mui/material/Box";
import Logger from "../../services/Logger";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import StatsService from "../../services/StatsGridService";
import StatsCrossService from "../../services/StatsCrossService";
import StatsWordWheelService from "../../services/StatsWordWheelService";

const TYPE_KEYS = ["WORD_SEARCH", "HIDDEN_MESSAGE", "CROSSWORD"];
const LANGUAGE_LABELS = { english: "English", french: "French" };

/** Merge two per-language stats (from grid and cross) into one words-puzzle block. */
function mergeLangStats(gLang, cLang) {
    const g = gLang || {};
    const c = cLang || {};
    const todayImported = (g.todayImported ?? 0) + (c.todayImported ?? 0);
    const reviewed = (g.reviewed ?? 0) + (c.reviewed ?? 0);
    const published = {
        total: (g.published?.total ?? 0) + (c.published?.total ?? 0),
        EASY: (g.published?.EASY ?? 0) + (c.published?.EASY ?? 0),
        MEDIUM: (g.published?.MEDIUM ?? 0) + (c.published?.MEDIUM ?? 0),
        HARD: (g.published?.HARD ?? 0) + (c.published?.HARD ?? 0),
    };
    const publishedByType = {};
    for (const type of TYPE_KEYS) {
        const gType = g.publishedByType?.[type] || {};
        const cType = c.publishedByType?.[type] || {};
        publishedByType[type] = {
            PAID: (gType.PAID ?? 0) + (cType.PAID ?? 0),
            FREE: (gType.FREE ?? 0) + (cType.FREE ?? 0),
        };
    }
    return { todayImported, reviewed, published, publishedByType };
}

function normalizeWordWheelLang(wLang) {
    const w = wLang || {};
    return {
        todayImported: w.todayImported ?? 0,
        reviewed: w.reviewed ?? 0,
        published: {
            total: w.published?.total ?? 0,
            EASY: w.published?.EASY ?? 0,
            MEDIUM: w.published?.MEDIUM ?? 0,
            HARD: w.published?.HARD ?? 0,
        },
        publishedByType: w.publishedByType || {},
    };
}

/**
 * Build overview split: words puzzles (grid+cross) and word wheel, each by language.
 */
function buildSplitOverview(gridData, crossData, wordWheelData) {
    const gByLang = gridData?.byLanguage || {};
    const cByLang = crossData?.byLanguage || {};
    const wByLang = wordWheelData?.byLanguage || {};
    const languages = [
        ...new Set([...Object.keys(gByLang), ...Object.keys(cByLang), ...Object.keys(wByLang)]),
    ].sort();
    const byLanguage = {};
    for (const lang of languages) {
        byLanguage[lang] = {
            words: mergeLangStats(gByLang[lang], cByLang[lang]),
            wordWheel: normalizeWordWheelLang(wByLang[lang]),
        };
    }
    return { byLanguage };
}

function paidTotalFromPublishedByType(publishedByType) {
    if (!publishedByType) return 0;
    return Object.values(publishedByType).reduce((sum, d) => sum + (d.PAID ?? 0), 0);
}

function difficultyPct(published, level) {
    const total = published?.total ?? 0;
    if (total <= 0) return 0;
    return Math.round(((published?.[level] ?? 0) / total) * 100);
}

function OverviewMetricCards({ data, daysCoveredDivisor = 2, daysCoveredHint }) {
    const stats = data || {
        todayImported: 0,
        reviewed: 0,
        published: { total: 0, EASY: 0, MEDIUM: 0, HARD: 0 },
        publishedByType: {},
    };
    const paidTotal = paidTotalFromPublishedByType(stats.publishedByType);
    const daysCovered = Math.floor((stats.reviewed ?? 0) / daysCoveredDivisor);
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: 2,
                width: "100%",
                alignItems: "stretch",
            }}
        >
            <Card
                sx={{
                    width: { xs: "100%", md: "19%" },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { md: "180px" },
                }}
            >
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ whiteSpace: "nowrap" }}>
                        Today&apos;s Imports
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#2196f3" }}>
                        {stats.todayImported ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: "auto" }}>
                        New puzzles imported
                    </Typography>
                </CardContent>
            </Card>

            <Card
                sx={{
                    width: { xs: "100%", md: "19%" },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { md: "180px" },
                }}
            >
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ whiteSpace: "nowrap" }}>
                        Days Covered
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#ff9800" }}>
                        {daysCovered}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: "auto" }}>
                        {daysCoveredHint}
                    </Typography>
                </CardContent>
            </Card>

            <Card
                sx={{
                    width: { xs: "100%", md: "44%" },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { md: "180px" },
                }}
            >
                <CardContent
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ whiteSpace: "nowrap" }}>
                        Published Difficulty
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            gap: 1,
                            flexGrow: 1,
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box sx={{ flex: "1 1 33.333%", textAlign: "center", minWidth: 0, overflow: "hidden" }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#4caf50",
                                    wordBreak: "break-word",
                                    fontSize: { xs: "1.5rem", sm: "2rem" },
                                }}
                            >
                                {difficultyPct(stats.published, "EASY")}%
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: "break-word", fontSize: "0.75rem" }}
                            >
                                Easy: {stats.published?.EASY ?? 0}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: "1 1 33.333%", textAlign: "center", minWidth: 0, overflow: "hidden" }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#ff9800",
                                    wordBreak: "break-word",
                                    fontSize: { xs: "1.5rem", sm: "2rem" },
                                }}
                            >
                                {difficultyPct(stats.published, "MEDIUM")}%
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: "break-word", fontSize: "0.75rem" }}
                            >
                                Medium: {stats.published?.MEDIUM ?? 0}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: "1 1 33.333%", textAlign: "center", minWidth: 0, overflow: "hidden" }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#f44336",
                                    wordBreak: "break-word",
                                    fontSize: { xs: "1.5rem", sm: "2rem" },
                                }}
                            >
                                {difficultyPct(stats.published, "HARD")}%
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: "break-word", fontSize: "0.75rem" }}
                            >
                                Hard: {stats.published?.HARD ?? 0}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Card
                sx={{
                    width: { xs: "100%", md: "18%" },
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { md: "180px" },
                }}
            >
                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom sx={{ whiteSpace: "nowrap" }}>
                        Total Published
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: "bold", color: "#4caf50" }}>
                        {stats.published?.total ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: "auto" }}>
                        Paid: {paidTotal}
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
}

export default function PuzzleOverview({ hideFrench = false }) {
    const [overviewData, setOverviewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadOverviewData();
    }, []);

    const loadOverviewData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [gridData, crossData, wordWheelData] = await Promise.all([
                StatsService.getOverviewStats(),
                StatsCrossService.getOverviewStats(),
                StatsWordWheelService.getOverviewStats(),
            ]);
            setOverviewData(buildSplitOverview(gridData, crossData, wordWheelData));
        } catch (err) {
            Logger.error("Error loading overview data:", err);
            setError("Failed to load overview data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                }}
            >
                <CircularProgress size={40} />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading overview statistics...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!overviewData?.byLanguage || Object.keys(overviewData.byLanguage).length === 0) {
        return null;
    }

    const langOrder = hideFrench ? ["english"] : ["english", "french"];

    return (
        <Grid size={{ xs: 12, md: 12 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
                {langOrder
                    .filter((lang) => overviewData.byLanguage[lang])
                    .map((lang) => {
                        const block = overviewData.byLanguage[lang];
                        const label = LANGUAGE_LABELS[lang] || lang;
                        return (
                            <Box key={lang} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                    {label}
                                </Typography>

                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                                    >
                                        Words puzzles
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                        Crossword, word search, hidden message
                                    </Typography>
                                    <OverviewMetricCards
                                        data={block?.words}
                                        daysCoveredDivisor={2}
                                        daysCoveredHint="Reviewed ÷ 2 (2 puzzles per day)"
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
                                    >
                                        Word wheel puzzles
                                    </Typography>
                                    <OverviewMetricCards
                                        data={block?.wordWheel}
                                        daysCoveredDivisor={1}
                                        daysCoveredHint="Reviewed (1 puzzle per day)"
                                    />
                                </Box>
                            </Box>
                        );
                    })}
            </Box>
        </Grid>
    );
}
